import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, Video, HelpCircle, Download, Bot, Send, Mail, Sparkles,
  ArrowRight, ArrowUpRight, Clock, Flame, GitBranch, PhoneCall,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { SectionLabel, TypeBadge } from "@/components/Common";

const CARD_ICONS = { sops: BookOpen, training: Video, faqs: HelpCircle, downloads: Download };

export default function Dashboard() {
  const { user, activeProgram } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [data, setData] = useState({ knowledge: [], videos: [], downloads: [], announcements: [], program: null });

  useEffect(() => {
    if (!activeProgram) return;
    const p = { params: { program: activeProgram } };
    Promise.all([
      api.get("/knowledge", p), api.get("/videos", p), api.get("/downloads", p),
      api.get("/announcements", p), api.get("/programs"),
    ]).then(([k, v, d, a, pr]) => {
      setData({
        knowledge: k.data, videos: v.data, downloads: d.data, announcements: a.data,
        program: pr.data.find((x) => x.id === activeProgram),
      });
    }).catch(() => {});
  }, [activeProgram]);

  const sops = data.knowledge.filter((x) => x.type === "sop");
  const faqs = data.knowledge.filter((x) => x.type !== "sop");
  const stats = [
    { key: "sops",      label: "SOP Articles",     count: sops.length,             to: "/app/sops" },
    { key: "training",  label: "Training Videos",  count: data.videos.length,      to: "/app/training" },
    { key: "faqs",      label: "FAQs & Workflows", count: faqs.length,             to: "/app/faqs" },
    { key: "downloads", label: "Downloads",        count: data.downloads.length,   to: "/app/downloads" },
  ];

  const recent = [...data.knowledge]
    .sort((a, b) => (b.last_updated || "").localeCompare(a.last_updated || ""))
    .slice(0, 4);

  const goChat = (e) => { e.preventDefault(); navigate("/app/chat", { state: { q } }); };

  const quickPrompts = [
    "How do I create and print a shipment label?",
    "Where do I upload the invoice?",
    "How do I perform putaway?",
    "Barcode won't scan — what do I do?",
  ];

  return (
    <div>
      {/* Editorial hero */}
      <section className="mb-14 kp-in">
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-primary flex items-center gap-2 mb-4">
          <Sparkles className="w-3.5 h-3.5" /> {data.program?.name || "Program"} · {data.program?.department}
        </p>
        <h1 className="font-display text-5xl md:text-[68px] leading-[1] tracking-tight text-foreground">
          How can we help <br />
          <span className="italic text-primary">{user?.name?.split(" ")[0]}</span> today?
        </h1>
        <p className="text-muted-foreground mt-4 max-w-2xl text-[15px] leading-relaxed">
          Ask a question, browse a section, or dive back into what you were reading.
          Everything here is grounded in your own SOPs, workflows and FAQs — no guesses.
        </p>

        <form onSubmit={goChat} className="mt-8 max-w-2xl kp-up" style={{ animationDelay: "80ms" }}>
          <div className="flex items-center gap-2 bg-card rounded-full border border-border p-2 pl-5 shadow-[0_10px_30px_-16px_hsl(224_54%_21%/0.25)] hover:border-primary/40 transition">
            <Bot className="w-5 h-5 text-primary shrink-0" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="dashboard-hero-search"
              placeholder="Ask anything — 'How do I upload the invoice?'"
              className="flex-1 min-w-0 outline-none text-[15px] text-foreground bg-transparent placeholder:text-muted-foreground"
            />
            <button type="submit" data-testid="dashboard-hero-submit"
              className="rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold flex items-center gap-1.5 hover:opacity-95 transition shrink-0">
              Ask <Send className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {quickPrompts.map((p) => (
              <button key={p} type="button" onClick={() => navigate("/app/chat", { state: { q: p } })}
                className="text-xs rounded-full border border-border bg-card/60 px-3 py-1.5 text-muted-foreground hover:border-primary/40 hover:text-primary transition-all">
                {p}
              </button>
            ))}
          </div>
        </form>
      </section>

      {/* Quick access */}
      <section className="mb-14">
        <SectionLabel icon={Flame}>Quick access</SectionLabel>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
          {stats.map((s) => {
            const Icon = CARD_ICONS[s.key];
            return (
              <button key={s.key} onClick={() => navigate(s.to)} data-testid={`stat-card-${s.key}`}
                className="text-left kp-card kp-card-lift p-5 group">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center group-hover:bg-primary transition-all">
                    <Icon className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="font-display text-4xl text-foreground mt-4 tabular-nums">{s.count}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{s.label}</p>
              </button>
            );
          })}
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-10">
        {/* Recently added */}
        <section className="lg:col-span-2">
          <SectionLabel icon={Clock} action={
            <button onClick={() => navigate("/app/sops")} className="text-xs text-primary font-semibold flex items-center gap-1 kp-link">
              Explore all <ArrowRight className="w-3.5 h-3.5" />
            </button>
          }>Recently added</SectionLabel>

          {recent.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground text-sm">
              Nothing published yet in this program.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 stagger" data-testid="recent-content-grid">
              {recent.map((item) => (
                <button key={item.id} onClick={() => navigate(item.type === "sop" ? "/app/sops" : "/app/faqs")}
                  className="text-left kp-card kp-card-lift p-5 group flex flex-col" data-testid={`recent-${item.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <TypeBadge type={item.type} />
                    <span className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {item.last_updated}
                    </span>
                  </div>
                  <h3 className="font-display text-lg text-foreground mt-3 leading-tight line-clamp-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2 flex-1 leading-relaxed">{item.content}</p>
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    {(item.tags || []).slice(0, 2).map((t) => (
                      <span key={t} className="text-[10px] font-mono uppercase tracking-wider rounded-full bg-muted px-2 py-0.5 text-muted-foreground">#{t}</span>
                    ))}
                    <span className="ml-auto text-xs text-primary font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all">
                      Read <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Sidebar column */}
        <aside className="space-y-8">
          {/* Assistant */}
          <button onClick={() => navigate("/app/chat")} data-testid="dashboard-assistant-card"
            className="w-full text-left rounded-2xl bg-primary text-primary-foreground p-6 hover:opacity-95 transition-all relative overflow-hidden group">
            <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-accent/20 blur-2xl" />
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-primary-foreground/10 border border-primary-foreground/15 flex items-center justify-center mb-4">
                <Bot className="w-5 h-5" />
              </div>
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-secondary/90">Grounded assistant</p>
              <h3 className="font-display text-2xl mt-1 leading-tight">Ask, get guided, done.</h3>
              <p className="text-sm text-primary-foreground/75 mt-2 leading-relaxed">
                Step-by-step help with sources — always from your published knowledge.
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-secondary mt-4 group-hover:translate-x-1 transition-transform">
                Open assistant <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </button>

          {/* What's new */}
          <div data-testid="dashboard-whatsnew-feed">
            <SectionLabel icon={Sparkles} action={
              <button onClick={() => navigate("/app/whats-new")} className="text-xs text-primary font-semibold kp-link">View all</button>
            }>What's new</SectionLabel>
            <div className="space-y-3">
              {data.announcements.slice(0, 3).map((a) => (
                <div key={a.id} className="kp-card p-4 kp-card-lift">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-wider rounded bg-secondary/70 text-primary px-1.5 py-0.5">{a.tag}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{a.date}</span>
                  </div>
                  <p className="text-sm font-semibold text-foreground leading-snug">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.description}</p>
                </div>
              ))}
              {data.announcements.length === 0 && (
                <p className="text-sm text-muted-foreground italic">Nothing new right now.</p>
              )}
            </div>
          </div>

          {/* PM / Need help */}
          <div data-testid="dashboard-pm-contact">
            <SectionLabel icon={PhoneCall}>Need help?</SectionLabel>
            <div className="kp-card p-5">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-secondary/60 text-primary font-semibold flex items-center justify-center">
                  {data.program?.pm_name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Program Manager</p>
                  <p className="text-sm font-semibold text-foreground truncate">{data.program?.pm_name}</p>
                </div>
              </div>
              <a href={`mailto:${data.program?.pm_email}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary font-semibold kp-link">
                <Mail className="w-3.5 h-3.5" /> {data.program?.pm_email}
              </a>
              <button onClick={() => navigate("/app/support")}
                className="mt-3 w-full text-xs font-semibold rounded-full border border-border bg-card px-4 py-2 hover:bg-secondary/40 hover:border-primary/30 transition flex items-center justify-center gap-1.5">
                All support contacts <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Explore by process */}
      <section className="mt-14">
        <SectionLabel icon={GitBranch}>Explore by content type</SectionLabel>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
          {[
            { label: "Standard Operating Procedures", type: "sop",   to: "/app/sops",      hint: "Step-by-step how-tos" },
            { label: "FAQs & Workflows",              type: "faq",   to: "/app/faqs",      hint: "Rules, edge cases, answers" },
            { label: "Training Videos",               type: "video", to: "/app/training",  hint: "Watch and learn" },
            { label: "Downloads & Forms",             type: "download", to: "/app/downloads", hint: "Templates & references" },
          ].map((c) => (
            <button key={c.type} onClick={() => navigate(c.to)}
              className="text-left kp-card kp-card-lift p-5 group">
              <TypeBadge type={c.type} size="lg" />
              <h3 className="font-display text-xl text-foreground mt-4 leading-tight">{c.label}</h3>
              <p className="text-sm text-muted-foreground mt-1">{c.hint}</p>
              <div className="mt-4 flex items-center text-xs font-semibold text-primary gap-1 group-hover:translate-x-1 transition-transform">
                Browse <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
